import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ClipboardList,
  FileText,
  Plus,
  ShieldAlert,
  Truck,
  UserCheck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  DASHBOARD_ALERT_TONE_CLASSES,
  buildDashboardSnapshot,
} from '../../core/dashboard-metrics';
import { formatTaskDate } from '../../core/task-filters';
import { hasPermission } from '../../core/permissions';
import type { AssignmentIndex } from '../../core/effective-status';
import { APP_CONFIG } from '../../core/config';
import { AUDIT_ACTION_LABELS } from '../../types/audit';
import type { Task } from '../../types/task';
import type { TaskTeam } from '../../types/assignment';
import type { Soldier, Vehicle } from '../../types/fleet';
import type { User } from '../../types/user';
import type { NavItemId } from '../../components/layout';
import { useDashboard } from './use-dashboard';
import { DriverWorkloadSection } from './DriverWorkloadSection';

interface DashboardPageProps {
  user: User;
  tasks: readonly Task[];
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  /** Every vehicle team, loaded once by the shell — see `useAssignments`. */
  teams: readonly TaskTeam[];
  /**
   * Who is out right now, derived once for every screen. Deliberately not
   * narrowed to today: a convoy that left yesterday and has not been closed is
   * still out, and the workload section has to say so.
   */
  assignments: AssignmentIndex;
  loading: boolean;
  onNavigate: (id: NavItemId) => void;
  /** Opens the task form on the board — the dashboard never owns the form. */
  onCreateTask: () => void;
}

/** One KPI widget: a headline number with the breakdown underneath it. */
function KpiCard({
  icon: Icon,
  title,
  value,
  hint,
  lines,
  bar,
}: {
  icon: LucideIcon;
  title: string;
  value: number | string;
  hint?: string;
  lines: readonly { label: string; value: number | string }[];
  bar?: { percent: number; label: string };
}) {
  return (
    <section className="bg-panel border border-line rounded-2xl p-4">
      <div className="flex items-center gap-2 text-ink/60">
        <Icon size={18} className="text-brand-500" aria-hidden />
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
      </div>
      <p className="mt-2 text-4xl font-bold tabular-nums leading-none">{value}</p>
      {hint ? <p className="text-xs text-ink/50 mt-1">{hint}</p> : null}

      {bar ? (
        <div className="mt-3">
          <div
            className="h-2 rounded-full bg-canvas overflow-hidden"
            role="progressbar"
            aria-valuenow={bar.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={bar.label}
          >
            <div className="h-full bg-brand-500" style={{ width: `${bar.percent}%` }} />
          </div>
          <p className="text-xs text-ink/50 mt-1">
            {bar.label} · {bar.percent}%
          </p>
        </div>
      ) : null}

      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        {lines.map((line) => (
          <div key={line.label} className="flex items-center justify-between gap-2">
            <dt className="text-ink/50 truncate">{line.label}</dt>
            <dd className="font-semibold tabular-nums">{line.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The operational command dashboard: the day's board, the readiness of the
 * fleet and of the drivers, the conflicts a manager took responsibility for
 * today, and the shortcuts into the work itself.
 *
 * Every number on this screen is derived by `src/core/dashboard-metrics.ts` —
 * the page counts nothing on its own. The workload section below the shortcuts
 * follows the same rule against its own engine, `src/core/driver-workload.ts`.
 */
export function DashboardPage({
  user,
  tasks,
  vehicles,
  soldiers,
  teams,
  assignments,
  loading,
  onNavigate,
  onCreateTask,
}: DashboardPageProps) {
  /** Fixed evaluation moment, so "היום" does not drift between renders. */
  const [now] = useState(() => Date.now());
  const { auditLogs, loading: extrasLoading } = useDashboard();

  const snapshot = useMemo(
    () =>
      buildDashboardSnapshot({
        tasks,
        vehicles,
        soldiers,
        teams,
        auditLogs,
        now,
      }),
    [tasks, vehicles, soldiers, teams, auditLogs, now],
  );

  const canCreate = hasPermission(user, 'CREATE_TASK');
  const canAssign = hasPermission(user, 'ASSIGN_TASK');
  const canViewReports = hasPermission(user, 'VIEW_REPORTS');
  const canViewAudit = hasPermission(user, 'VIEW_AUDIT_LOGS');

  const shortcuts = [
    canCreate
      ? { id: 'create', icon: Plus, label: 'פתיחת משימה חדשה', hint: 'טופס משימה מלא', run: onCreateTask }
      : null,
    canAssign
      ? {
          id: 'assign',
          icon: Truck,
          label: 'שיבוץ רכבים וצוותים',
          hint: 'לוח המשימות',
          run: () => onNavigate('tasks'),
        }
      : null,
    canViewReports
      ? {
          id: 'dispatch',
          icon: FileText,
          label: 'דף שילוח יומי',
          hint: 'הדפסה ו-PDF',
          run: () => onNavigate('reports'),
        }
      : null,
    {
      id: 'fleet',
      icon: Users,
      label: 'מצבת רכבים וחיילים',
      hint: 'זמינות ורישיונות',
      run: () => onNavigate('vehicles'),
    },
  ].flatMap((shortcut) => (shortcut ? [shortcut] : []));

  if (loading || extrasLoading) {
    return <p className="text-sm text-ink/60">טוען את מצב היחידה…</p>;
  }

  const { tasks: taskKpis, fleet, drivers, overrides, alerts } = snapshot;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <section className="bg-panel border border-line rounded-2xl p-5">
        <p className="text-xs text-ink/50">{APP_CONFIG.appName}</p>
        <h2 className="text-xl font-bold">לוח בקרה מבצעי</h2>
        <p className="text-sm text-ink/60 mt-1">
          {formatTaskDate(snapshot.dateKey, now)} · {snapshot.dateKey} · שלום {user.fullName}
        </p>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard
          icon={ClipboardList}
          title="משימות היום"
          value={taskKpis.total}
          hint={`${taskKpis.open} עדיין על הלוח`}
          lines={[
            { label: 'בביצוע', value: taskKpis.inProgress },
            { label: 'הושלמו', value: taskKpis.completed },
            { label: 'בוטלו', value: taskKpis.cancelled },
            { label: 'דחופות פתוחות', value: taskKpis.urgent },
          ]}
        />
        <KpiCard
          icon={Truck}
          title="כשירות צי הרכבים"
          value={fleet.available}
          hint={`מתוך ${fleet.total} רכבים במצבת · ${fleet.assigned} במשימה כעת`}
          bar={{ percent: fleet.readyPercent, label: 'זמינים כעת' }}
          lines={[
            { label: 'משובצים', value: fleet.assigned },
            { label: 'בטיפול', value: fleet.maintenance },
            { label: 'מושבתים', value: fleet.grounded },
            { label: 'זמינים', value: fleet.available },
          ]}
        />
        <KpiCard
          icon={UserCheck}
          title="זמינות נהגים"
          value={drivers.readyDrivers}
          hint={`${drivers.licensed} בעלי רישיון צבאי מתוך ${drivers.total}`}
          lines={[
            { label: 'זמינים', value: drivers.available },
            { label: 'במשימה', value: drivers.onMission },
            { label: 'בחופשה', value: drivers.leave },
            { label: 'לא זמינים', value: drivers.unavailable },
          ]}
        />
        <KpiCard
          icon={ShieldAlert}
          title="עקיפות והתראות היום"
          value={overrides.overrides}
          hint={`${overrides.critical} מהן על ממצא חמור`}
          lines={[
            { label: 'רשומות ביומן', value: overrides.logged },
            { label: 'התראות פתוחות', value: alerts.length },
            { label: 'ללא רכב וצוות', value: taskKpis.unassigned },
            { label: 'ממצאים חמורים', value: overrides.critical },
          ]}
        />
      </div>

      <section className="bg-panel border border-line rounded-2xl p-4">
        <h3 className="text-sm font-semibold mb-3">פעולות מהירות</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {shortcuts.map((shortcut) => {
            const Icon = shortcut.icon;
            return (
              <button
                key={shortcut.id}
                type="button"
                onClick={shortcut.run}
                className="h-16 rounded-xl border border-line bg-canvas hover:border-brand-300 hover:bg-brand-50 transition-colors px-3 flex items-center gap-3 text-right"
              >
                <Icon size={18} className="text-brand-500 shrink-0" aria-hidden />
                <span className="min-w-0">
                  <span className="block text-sm font-medium truncate">{shortcut.label}</span>
                  <span className="block text-xs text-ink/50 truncate">{shortcut.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* A section of its own, and not part of the driver-availability widget
          above it: that one answers "who is here", this one answers "who has
          already done the most today". See `DriverWorkloadSection`. */}
      <DriverWorkloadSection
        user={user}
        soldiers={soldiers}
        tasks={tasks}
        teams={teams}
        assignments={assignments}
      />

      <section id="field-alerts" className="bg-panel border border-line rounded-2xl p-4">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-500" aria-hidden />
          התראות שטח פעילות
        </h3>
        {alerts.length === 0 ? (
          <p className="text-sm text-ink/60">אין התראות פתוחות — כל משימות היום משובצות ומתקדמות.</p>
        ) : (
          <ul className="space-y-2">
            {alerts.map((alert) => (
              <li
                key={alert.id}
                className={[
                  'flex items-center justify-between gap-3 rounded-xl border p-3 text-sm',
                  DASHBOARD_ALERT_TONE_CLASSES[alert.tone],
                ].join(' ')}
              >
                <span>{alert.label}</span>
                <span className="font-bold tabular-nums text-base">{alert.count}</span>
              </li>
            ))}
          </ul>
        )}

        {canViewAudit && overrides.entries.length > 0 ? (
          <>
            <h4 className="text-sm font-semibold mt-4 mb-2">שיבוצים שאושרו בכל זאת היום</h4>
            <ul className="space-y-1.5 text-xs text-ink/70">
              {overrides.entries.slice(0, 5).map((entry) => (
                <li key={entry.id} className="rounded-xl border border-line p-2.5">
                  {AUDIT_ACTION_LABELS[entry.action]} — {entry.summary}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>
    </div>
  );
}
